import { useCallback, useState, useRef, useEffect } from "react";
import {
  validate,
  EditComponentErrorResponse,
  FormSchemaDto,
  FieldSchemaDto,
  FieldGroupDto,
} from "@amzn/crowd-front-end-common-library";
import {
  FormSchemaBase,
  FieldGroupBase,
  FieldSchemaBase,
  FieldValidationRules,
} from "@amzn/crowd-front-end-common-library/dist/src/forms/shared";
import { AxiosError } from "axios";
import { MetricsState } from "../../logging";
import { MoveTeamInputStepResult as InputStepResult } from "./MoveTeamInputStepResult";
import { TeamDto } from "../../common/model/team.dto";
import useAsync from "../../http/useAsync";
import httpRequestWithMetrics from "../../http/httpRequestWithMetrics";
import { LoadingState } from "../../http";

interface UseMoveTeamValidateHookResult {
  execute: () => void;
  loadingState: LoadingState;
  validationErrors?: EditComponentErrorResponse;
}

function syncValidate(
  modifiedTeam: Partial<InputStepResult>,
  formSchema: FormSchemaBase<FieldSchemaDto, FieldGroupBase<FieldSchemaDto>>,
  parentTeamId: string,
  parentTeamName: string,
  currentTeam: TeamDto
): EditComponentErrorResponse {
  const teamGroup = formSchema.groups.find(
    (group) => group.groupLabel === "Parent team"
  ) as FieldGroupDto;
  const sameParentTeamValidationRules = (teamGroup.fieldDefinitions.find(
    (field) => field.propertyName === "newParentTeamId"
  ) as FieldSchemaBase).validationRules as FieldValidationRules;
  const exclusionRule =
    currentTeam.teamId === modifiedTeam.newParentTeamId
      ? {
          within: [currentTeam.teamId],
          message: `^A team cannot be its own parent. Select a different parent team.`,
        }
      : {
          within: [parentTeamId],
          message: `^${parentTeamName} is already the parent team for ${currentTeam.name}. Please select a different parent team.`,
        };

  sameParentTeamValidationRules.exclusion = exclusionRule;
  return validate(modifiedTeam, formSchema);
}

async function validateInputAsync(
  localChanges: Partial<InputStepResult>,
  currentTeam: TeamDto,
  metrics: MetricsState
): Promise<any> {
  const team = { ...currentTeam, reportsTo: localChanges.newParentTeamId };

  const response = await httpRequestWithMetrics(
    "moveTeamValidateAsync",
    metrics,
    {
      url: `/api/v1/teams/validate`,
      method: "POST",
      data: {
        team,
      },
    }
  );
  return response.data;
}

/**
 * Custom react hook that abstracts the validation logic for move team workflow.
 *
 * @param {FormSchemaDto} formSchema
 * @param {string} parentTeamId
 * @param {string} parentTeamName
 * @param {Partial<InputStepResult>} localChanges
 * @param {TeamDto} currentTeam
 * @param {MetricsState} metrics
 * @param {(localChanges: Partial<InputStepResult>) => void} onPassValidation
 * @returns {UseMoveTeamValidateHookResult}
 */
function useMoveTeamValidate(
  formSchema: FormSchemaDto,
  parentTeamId: string,
  parentTeamName: string,
  localChanges: Partial<InputStepResult>,
  currentTeam: TeamDto,
  metrics: MetricsState,
  onPassValidation: (localChanges: Partial<InputStepResult>) => void
): UseMoveTeamValidateHookResult {
  const changesToValidate = useRef<Partial<InputStepResult>>();
  const validatedChanges = useRef<Partial<InputStepResult>>();
  const [validationErrors, setValidationErrors] = useState<
    EditComponentErrorResponse
  >();

  const {
    loadingState,
    execute: executeAsyncValidate,
    error,
    response: asyncValidateResult,
  } = useAsync(validateInputAsync, [localChanges, currentTeam, metrics]);

  useEffect(() => {
    const httpError = error as AxiosError;
    if (httpError && httpError.response && httpError.response.status === 400) {
      // 400 Errors are validation errors. Treat other errors differently.
      setValidationErrors(httpError.response.data);
    }
  }, [error]);

  useEffect(() => {
    if (asyncValidateResult && asyncValidateResult.errors.length > 0) {
      const errors = asyncValidateResult.errors.map(
        (asyncError: { attribute: string; errorMessage: string }) => {
          return {
            fieldName: "newParentTeamId",
            message: asyncError.errorMessage,
            validation: "asyncValidation",
          };
        }
      );

      const asyncError: EditComponentErrorResponse = {
        errors: [
          {
            groupId: "general-group",
            groupLabel: "Parent team",
            errors,
          },
        ],
      };
      setValidationErrors(asyncError);
    } else if (
      loadingState === LoadingState.RESOLVED &&
      changesToValidate.current &&
      asyncValidateResult.errors.length === 0
    ) {
      validatedChanges.current = changesToValidate.current;
      setValidationErrors(undefined);
      onPassValidation(changesToValidate.current);
    }
  }, [loadingState, onPassValidation, asyncValidateResult]);

  const execute = useCallback(() => {
    if (validatedChanges.current === localChanges) {
      // Instantly validate changes that have already been validated.
      onPassValidation(validatedChanges.current);
      return;
    }

    const modifiedTeam = { ...localChanges };
    const syncValidateResult = syncValidate(
      modifiedTeam,
      formSchema,
      parentTeamId,
      parentTeamName,
      currentTeam
    );

    if (syncValidateResult.errors.length > 0) {
      setValidationErrors(syncValidateResult);
    } else {
      changesToValidate.current = localChanges;
      setValidationErrors(undefined);
      executeAsyncValidate();
    }
  }, [
    localChanges,
    onPassValidation,
    formSchema,
    executeAsyncValidate,
    parentTeamId,
    parentTeamName,
    currentTeam,
  ]);

  return {
    execute,
    loadingState,
    validationErrors,
  };
}

export default useMoveTeamValidate;
